import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./MISSendEmailModal.css";
import MISInvoiceInbox from "./MISInvoiceInbox";

const API = `${process.env.REACT_APP_BASE_URL}/api/mis/email`;
const MAX_OPTIONAL_FILES = 8; // MIS Excel + PDF are attached separately by the server.
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_TOTAL_BYTES = 15 * 1024 * 1024;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const monthTitle = (month) => {
  if (!/^\d{4}-\d{2}$/.test(month || "")) return month || "";
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-IN", {
    month: "long",
    year: "numeric",
  });
};
const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};
const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      if (comma < 0) return reject(new Error(`Could not encode ${file.name}.`));
      resolve(result.slice(comma + 1));
    };
    reader.readAsDataURL(file);
  });

export default function MISSendEmailModal({
  clientId,
  clientName,
  month,
  onClose,
  onSent,
}) {
  const [status, setStatus] = useState({
    loading: true,
    connected: false,
    email: "",
  });
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(
    `Monthly MIS Report – ${clientName} – ${monthTitle(month)}`,
  );
  const [message, setMessage] = useState(
    `Dear ${clientName},\n\nPlease find attached the MIS report for ${monthTitle(month)} in both Excel and PDF formats.\n\nKindly review and let us know if you have any questions.\n\nRegards`,
  );
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [showInvoiceInbox, setShowInvoiceInbox] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [readingFiles, setReadingFiles] = useState(false);
  const [includeExcel, setIncludeExcel] = useState(true);
  const [includePdf, setIncludePdf] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/google/status`, { headers: authHeaders() })
      .then(({ data }) => {
        if (alive)
          setStatus({
            loading: false,
            connected: Boolean(data.connected),
            email: data.email || "",
          });
      })
      .catch((e) => {
        if (alive) {
          setStatus({ loading: false, connected: false, email: "" });
          setError(
            e.response?.data?.message || "Unable to check Google connection.",
          );
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  const connectGoogle = async () => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/google/connect`, {
        headers: authHeaders(),
      });
      if (!data.url)
        throw new Error("Server did not return a Google authorization URL.");
      window.location.assign(data.url);
    } catch (e) {
      setError(
        e.response?.data?.message || e.message || "Could not connect Google.",
      );
    }
  };

  const addAttachments = async (event) => {
    const selected = Array.from(event.target.files || []);
    // Clear the input so the same file can be selected again after removal.
    event.target.value = "";
    if (!selected.length) return;
    setError("");
    if (attachments.length + selected.length > MAX_OPTIONAL_FILES) {
      setError(
        `You can add up to ${MAX_OPTIONAL_FILES} optional files. The MIS Excel and PDF are attached automatically.`,
      );
      return;
    }
    for (const file of selected) {
      if (file.size <= 0) {
        setError(`${file.name} is empty.`);
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        setError(`${file.name} exceeds the 10 MB per-file limit.`);
        return;
      }
    }
    const currentTotal = attachments.reduce((sum, file) => sum + file.size, 0);
    const selectedTotal = selected.reduce((sum, file) => sum + file.size, 0);
    if (currentTotal + selectedTotal > MAX_TOTAL_BYTES) {
      setError("Optional attachments together must be 15 MB or smaller.");
      return;
    }
    setReadingFiles(true);
    try {
      const encoded = await Promise.all(
        selected.map(async (file) => ({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
          filename: file.name,
          mimeType: file.type || "application/octet-stream",
          size: file.size,
          contentBase64: await readFileAsBase64(file),
        })),
      );
      setAttachments((previous) => [...previous, ...encoded]);
    } catch (e) {
      setError(e.message || "Unable to read selected attachments.");
    } finally {
      setReadingFiles(false);
    }
  };

  const removeAttachment = (id) =>
    setAttachments((previous) => previous.filter((file) => file.id !== id));

  const send = async (event) => {
    event.preventDefault();
    setError("");
    setSending(true);
    try {
      const optionalAttachments = attachments.map(
        ({ filename, mimeType, contentBase64 }) => ({
          filename,
          mimeType,
          contentBase64,
        }),
      );
      await axios.post(
        `${API}/send`,
        {
          clientId,
          month,
          to,
          cc,
          subject,
          message,
          attachments: optionalAttachments,
          includeExcel,
          includePdf,
        },
        {
          headers: { ...authHeaders(), "Content-Type": "application/json" },
        },
      );
      await onSent?.();
    } catch (e) {
      setError(
        e.response?.data?.message ||
          "MIS email could not be sent. Please check the connection and try again.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div
        className="mis-email-backdrop"
        role="presentation"
        onMouseDown={(e) =>
          e.target === e.currentTarget && !sending && onClose?.()
        }
      >
        <section
          className="mis-email-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mis-email-title"
        >
          <header className="mis-email-header">
            <div>
              <h2 id="mis-email-title">Send Monthly MIS</h2>
              <p>
                {clientName} · {monthTitle(month)}
              </p>
            </div>
            <button
              type="button"
              className="mis-email-close"
              onClick={onClose}
              disabled={sending}
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <form onSubmit={send}>
            <div className="mis-email-content">
              <div className="mis-email-google">
                <div>
                  <strong>
                    {status.loading
                      ? "Checking Google connection…"
                      : status.connected
                        ? `Connected: ${status.email}`
                        : "Google account not connected"}
                  </strong>
                  <small>
                    Emails are sent from the connected Gmail account.
                  </small>
                </div>
                <button
                  type="button"
                  className="mis-email-secondary"
                  onClick={connectGoogle}
                  disabled={status.loading || sending}
                >
                  {status.connected ? "Reconnect Google" : "Connect Google"}
                </button>
              </div>
              <div className="mis-email-inbox-action">
                <div>
                  <strong>Invoice &amp; payment emails</strong>
                  <small>
                    Search the connected Gmail inbox for invoice, billing,
                    payment, receipt, and remittance emails.
                  </small>
                </div>
                <button
                  type="button"
                  className="mis-email-secondary"
                  onClick={() => setShowInvoiceInbox(true)}
                  disabled={sending}
                >
                  View inbox
                </button>
              </div>
              <label>
                To <span>*</span>
                <input
                  required
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="client@example.com"
                />
                <small>
                  Separate multiple addresses with commas or semicolons.
                </small>
              </label>
              <label>
                CC
                <input
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  placeholder="accounts@example.com, manager@example.com"
                />
              </label>
              <label>
                Subject <span>*</span>
                <input
                  required
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={250}
                />
              </label>
              <label>
                Message <span>*</span>
                <textarea
                  required
                  rows={7}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={20000}
                />
              </label>

              <div className="mis-email-attachments">
                <strong>Attachments</strong>

                <label className="mis-email-attachment-option">
                  <input
                    type="checkbox"
                    checked={includeExcel}
                    onChange={(e) => setIncludeExcel(e.target.checked)}
                    disabled={sending}
                  />
                  <span>
                    📊 MIS_
                    {(clientName || "Client").replace(/[^a-z0-9_-]/gi, "_")}_
                    {month}.xlsx
                    <small> — Include Excel report</small>
                  </span>
                </label>

                <label className="mis-email-attachment-option">
                  <input
                    type="checkbox"
                    checked={includePdf}
                    onChange={(e) => setIncludePdf(e.target.checked)}
                    disabled={sending}
                  />
                  <span>
                    📄 MIS_
                    {(clientName || "Client").replace(/[^a-z0-9_-]/gi, "_")}_
                    {month}.pdf
                    <small> — Include PDF report</small>
                  </span>
                </label>

                <small>
                  Uncheck either report to exclude it from the email. The Excel
                  and PDF reports use the existing included MIS records and
                  totals.
                </small>

                <div className="mis-email-extra-attachments">
                  <div className="mis-email-extra-attachments-heading">
                    <strong>Additional files (optional)</strong>

                    <button
                      type="button"
                      className="mis-email-secondary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={
                        sending ||
                        readingFiles ||
                        attachments.length >= MAX_OPTIONAL_FILES
                      }
                    >
                      {readingFiles ? "Reading files…" : "＋ Add attachments"}
                    </button>

                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      hidden
                      onChange={addAttachments}
                    />
                  </div>

                  <small>
                    Up to {MAX_OPTIONAL_FILES} extra files · 10 MB per file · 15
                    MB combined.
                  </small>

                  {attachments.length > 0 && (
                    <ul className="mis-email-attachment-list">
                      {attachments.map((file) => (
                        <li key={file.id}>
                          <span
                            className="mis-email-attachment-name"
                            title={file.filename}
                          >
                            {file.filename}{" "}
                            <small>({formatBytes(file.size)})</small>
                          </span>

                          <button
                            type="button"
                            className="mis-email-remove-attachment"
                            onClick={() => removeAttachment(file.id)}
                            disabled={sending}
                            aria-label={`Remove ${file.filename}`}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {error && (
                <div className="mis-email-error" role="alert">
                  {error}
                </div>
              )}
            </div>
            <footer className="mis-email-footer">
              <button
                type="button"
                className="mis-email-secondary"
                onClick={onClose}
                disabled={sending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="mis-email-primary"
                disabled={
                  sending || readingFiles || status.loading || !status.connected
                }
              >
                {sending ? "Sending…" : "Send MIS"}
              </button>
            </footer>
          </form>
        </section>
      </div>
      {showInvoiceInbox && (
        <MISInvoiceInbox onClose={() => setShowInvoiceInbox(false)} />
      )}
    </>
  );
}
